import mixpanel from 'mixpanel-browser';
import Auth from "./Auth";
import Newsletter from "./Newsletter";
import Api from "./Api";
import Locale from "./Locale";
import flagsmith from "flagsmith";
import {OpenFeature, ProviderEvents} from "@openfeature/js-sdk";
import isEmpty from "lodash/isEmpty";

const mixpanelToken = process.env.REACT_APP_MIXPANEL_TOKEN;

export const initMixpanel = () => {
  if (!mixpanelToken) {
    console.warn('Mixpanel token is missing! Check your .env file.');
    return;
  }

  mixpanel.init(mixpanelToken, {
    persistence: "localStorage",
    api_host: 'https://api-eu.mixpanel.com',
    autocapture: true,
    track_pageview: true
  });

  OpenFeature.addHandler(ProviderEvents.Ready, () => {
    if (Auth.isLoggedIn()) {
      mixpanelIdentifyAccount(Auth.getAccountId());
      waitForUserData((userData) => {
        mixpanel.people.set(userData);
      });
    }
  });
}

const getUserData = () => {
  const flags = flagsmith.getAllFlags();
  const email = Auth.getEmail() || Newsletter.getLocalEntry()?.email;

  let userData = {
    "$browser": window.navigator.userAgent,
    "session_id": Api.getSessionId(),
  };

  if (isEmpty(flags)) {
    return null;
  }

  for (let key in flags) {
    const flag = flags[key];

    if (flag.enabled && flag.value) {
      userData[key] = flag.value;
    } else {
      userData[key] = flag.enabled;
    }
  }

  if (email) {
    userData["$email"] = email;
  }

  if (Auth.isLoggedIn()) {
    userData["$user_id"] = Auth.getAccountId();
    userData["account_type"] = Auth.getAccountType();
  }

  if (Newsletter.getLocalEntry()?.email) {
    userData["newsletter_email"] = Newsletter.getLocalEntry()?.email;
  }

  if (Newsletter.getLocalEntry()?.firstname) {
    userData["$first_name"] = Newsletter.getLocalEntry().firstname;
  }

  if (Newsletter.getLocalEntry()?.lastname) {
    userData["$last_name"] = Newsletter.getLocalEntry()?.lastname;
  }

  if (Locale.getCountry() !== 'INT') {
    userData["$country_code"] = Locale.getCountry();
  }

  if (Locale.getLanguage()) {
    userData["language"] = Locale.getLanguage();
  }

  return userData;
};

const waitForUserData = (callback, maxRetries = 30, retryDelay = 200) => {
  const attemptFetchUserData = (attempts = 0) => {
    const userData = getUserData();

    if (userData) {
      callback(userData);  // Call the passed callback with userData when it's available
    } else if (attempts < maxRetries) {
      setTimeout(() => attemptFetchUserData(attempts + 1), retryDelay);
    } else {
      console.warn("User data not available after several attempts.");
    }
  };

  attemptFetchUserData();
};

export const trackViewSubscriptions = (subscriptionType, extension = false) => {
  waitForUserData((userData) => {
    mixpanel.track("View Subscription List", {
      subscription_type: subscriptionType,
      is_extension: extension,
      ...userData
    });
  });
};

export const trackViewLandingPage = (pageType = 'bikefit') => {
  waitForUserData((userData) => {
    mixpanel.track("View Landing Page", {
      ...userData,
      'landing_page': pageType
    });
  });
};

export const trackBikefitCTA = (pageType = 'bikefit') => {
  waitForUserData((userData) => {
    mixpanel.track("Bikefit CTA", {
      ...userData,
      'source': pageType
    });
  });
};

export const trackSubscriptionCheckout = ({ code, type, price, extension }) => {
  waitForUserData((userData) => {
    mixpanel.track("Subscription Checkout", {
      subscription_code: code,
      subscription_type: type,
      price: price.price / 100,
      currency: price.currency,
      is_extension: extension,
      ...userData
    });
  });
};

export const mixpanelIdentifyAccount = (accountId = null) => {
  if (accountId) {
    mixpanel.identify(accountId);
  }
};

export const mixpanelReset = () => {
  mixpanel.reset();
};
